import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, TextField } from '../../ui';
import { useSession } from '../../state/useSession';
import { takeUpEmailLink, useBuildRun } from '../../state/buildRun';
import { readIntent } from '../../state/buildIntent';
import { useWhat } from '../../state/what';
import { openReadyLink, readyLinkRefusal, requestReadyLink, type ReadyLink } from '../../api/readyLink';
import { finishProfile, signInWithLink } from '../../api/auth';
import { ApiError } from '../../api/client';
import { demoForIdea, emailNoun } from '../../journey/demos';
import { TYPES } from '../../journey/questions';
import { funnelStep } from '../../analytics/funnel';
import { trackPixel } from '../../analytics/metaPixel';
import SignUp from '../auth/SignUp';

type Stage =
  | { at: 'opening' }
  | { at: 'expired' }
  | { at: 'trouble' }
  | { at: 'code'; link: ReadyLink }
  | { at: 'failed'; link: ReadyLink }
  | { at: 'finish'; link: ReadyLink }
  | { at: 'collecting'; link: ReadyLink };

/**
 * /new/ready?t=… — the "Your … is ready" email's link (V7, docs/superpowers/specs/2026-10-16-v7-build.md;
 * V7-A/07-finish). Most likely another browser than the one the build began in.
 *
 *  1. The token is traded for its build (POST /waiting/ready-open), which also makes sure an account
 *     exists for the address, made verified. The token leaves the address bar at once.
 *  2. It signs them in: Cognito's custom challenge, answered with the token (api/auth.ts).
 *  3. This browser takes the build up as the V6 emailed link did (state/buildRun.ts takeUpEmailLink).
 *  4. A NEW account is asked to finish: "Your email is confirmed", the Terms (no name: the client may not write one),
 *     "Create my account" — or Apple. An existing account goes straight on.
 *  5. The build is collected under its key: the server releases it as the free week and the live
 *     screen opens (/new/live/<id>), or, still building, it is waited for here.
 *
 * A link that is bad, used or past its three days: "This link has expired", with the email field to
 * send a new one when this browser has the build. A build that failed: "It didn't build. Try again".
 */
export default function Ready() {
  const navigate = useNavigate();
  const { adopt, user } = useSession();
  const [params, setParams] = useSearchParams();
  // Read once: the token is taken out of the address (and every Meta URL) on arrival.
  const [token] = useState(() => params.get('t') ?? '');
  const [stage, setStage] = useState<Stage>(() => (token ? { at: 'opening' } : { at: 'expired' }));

  /*
   * Once per page, even where React runs an effect twice (development): a token works once, so a
   * second open-and-sign-in would find it used. The work is kept in a ref and its answer lands
   * while the screen is still mounted.
   */
  const started = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (params.has('t')) setParams((p) => { p.delete('t'); return p; }, { replace: true });
    if (started.current) return;
    started.current = true;
    if (!token) {
      funnelStep('ready_link_opened', { code: 'expired' });
      return;
    }
    const land = (next: Stage): void => {
      if (mounted.current) setStage(next);
    };
    void (async () => {
      let link: ReadyLink;
      try {
        link = await openReadyLink(token);
      } catch (err) {
        // 400 bad_link: past its three days, or not ours. 502: the account could not be made just now.
        const trouble = err instanceof ApiError && (err.kind === 'server' || err.kind === 'offline' || err.kind === 'timeout');
        funnelStep('ready_link_opened', { code: trouble ? 'error' : 'expired' });
        land(trouble ? { at: 'trouble' } : { at: 'expired' });
        return;
      }
      if (link.build === 'failed') {
        funnelStep('ready_link_opened', { code: 'failed' });
        land({ at: 'failed', link });
        return;
      }
      // Taken up first, so whichever sign-in follows — the link's, or the emailed code's — collects it.
      if (link.build !== 'gone') {
        takeUpEmailLink({
          anonId: link.anonId,
          requestKey: link.requestKey,
          goal: link.goal,
          appName: link.appName,
          plan: link.plan ?? null,
          email: link.email,
          state: link.build === 'done' ? 'done' : 'running',
          createdAt: '',
        });
      }
      /*
       * A link signs in once. Opened before (linkUsed), or refused by Cognito, the emailed code does
       * it instead: today's sign-up, the address already in it.
       */
      let signedIn: Awaited<ReturnType<typeof signInWithLink>> | null = null;
      if (!link.linkUsed) {
        try {
          signedIn = await signInWithLink(link.email, token);
        } catch {
          signedIn = null;
        }
      }
      if (!signedIn) {
        funnelStep('ready_link_opened', { code: 'code_needed' });
        land({ at: 'code', link });
        return;
      }
      if (mounted.current) adopt(signedIn);
      funnelStep('ready_link_opened', { code: link.build });
      if (!link.newAccount) funnelStep('signin_completed', { method: 'email' });
      // Collected already (another browser, or this link's first visit): their apps.
      if (link.build === 'gone') {
        if (mounted.current) navigate('/my-apps', { replace: true });
        return;
      }
      land(link.newAccount ? { at: 'finish', link } : { at: 'collecting', link });
    })();
    // Once, for the link the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Collected under its key, as Building does after a sign-in: a held build is released into the free
   * week and the live screen opens (state/buildRun.ts navigates there); a running one is waited for.
   * Anything else — a held preview, an offer, an error — is the build screen's to show.
   */
  const collecting = useRef(false);
  useEffect(() => {
    if (stage.at !== 'collecting' || !user || collecting.current) return;
    collecting.current = true;
    let live = true;
    void useBuildRun.getState().collect(user.id).then((found) => {
      /*
       * Only what the build screen shows: nothing to collect, a refusal or an offer, a held preview.
       * A release has already navigated to the live screen itself (and a subscriber's build to the
       * editor), a moment BEFORE this resolves and before this screen has unmounted — going on to the
       * build screen then replaced the live screen with "Nothing is building" (journey capture,
       * 2026-10-16).
       */
      const after = useBuildRun.getState();
      if (live && (!found || after.status === 'error' || after.heldApp)) navigate('/new/building', { replace: true });
    });
    return () => {
      live = false;
    };
  }, [stage.at, user, navigate]);

  if (stage.at === 'opening') {
    return (
      <Screen>
        <div className="ready-wait-screen" role="status">
          <span className="building-going-live-spinner" aria-hidden="true" />
          <p className="type-body">Opening your app…</p>
        </div>
      </Screen>
    );
  }

  if (stage.at === 'expired') return <Expired />;

  if (stage.at === 'trouble') {
    return (
      <Screen title="We couldn’t open it just now">
        <p className="type-body ready-lead">Your app is safe. Try the link again in a moment.</p>
        <Button title="Try again" onPress={() => window.location.reload()} />
      </Screen>
    );
  }

  if (stage.at === 'code') {
    const gone = stage.link.build === 'gone';
    return (
      <Screen>
        <SignUp
          card={{
            from: gone ? '/my-apps' : '/new/building',
            headline: `Sign in to see your ${stage.link.appName || 'app'}`,
            sub: 'That link has been used. We’ll email you a code instead. No password needed.',
            email: stage.link.email,
          }}
        />
      </Screen>
    );
  }

  if (stage.at === 'failed') {
    return (
      <Screen title="It didn’t build">
        <p className="type-body ready-lead">Something went wrong building {stage.link.appName || 'your app'}. Nothing was charged.</p>
        <Button
          title="Try again"
          onPress={() => {
            // Their sentence back in the field, kept as theirs: a type switch will not replace it.
            useWhat.setState({ prompt: stage.link.goal, filled: '', step: 0 });
            navigate('/new/what');
          }}
        />
      </Screen>
    );
  }

  if (stage.at === 'finish') {
    return <Finish link={stage.link} onDone={() => setStage({ at: 'collecting', link: stage.link })} />;
  }

  const running = stage.link.build === 'running';
  return (
    <Screen>
      <div className="ready-wait-screen" role="status" aria-live="polite">
        <span className="building-going-live-spinner" aria-hidden="true" />
        <p className="type-heading">{running ? 'Still building. It opens here when it’s done.' : `Your ${stage.link.appName || 'app'} is built.`}</p>
        <p className="type-body ready-lead">{running ? 'Usually about 4 minutes in all.' : 'Putting it live…'}</p>
      </div>
    </Screen>
  );
}

/** What the app is, in a word, for "keep your …": from the ad it came from, else from its words. */
export function thingFor(goal: string): string {
  const demo = demoForIdea(goal);
  if (demo) return TYPES[demo.type].thing;
  if (/\bgolf\b/i.test(goal)) return TYPES.golf.thing;
  if (/\b(rsvp|wedding)\b/i.test(goal)) return TYPES.rsvp.thing;
  if (/\b(schedule|shifts?|rota|volunteers?)\b/i.test(goal)) return TYPES.scheduler.thing;
  return 'app';
}

/** V7-A/07-finish: the account the link made, finished in one press. No password, no code. */
function Finish({ link, onDone }: { link: ReadyLink; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const thing = thingFor(link.goal);
  const running = link.build === 'running';

  async function create(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      // Pressing it IS accepting the Terms said directly above it, as on sign-up.
      await finishProfile({ acceptedTermsAt: new Date().toISOString() });
    } catch {
      // The account is theirs and signed in either way; a stamp not written is not a reason to stop here.
      funnelStep('error_shown', { where: 'finish_account', code: 'profile' });
    }
    trackPixel('CompleteRegistration');
    funnelStep('signup_completed', { method: 'email' });
    funnelStep('account_finished', { method: 'email' });
    onDone();
  }

  return (
    <Screen>
      <p className="ready-confirmed type-caption" role="status">
        <span aria-hidden="true">✓ </span>Your email is confirmed.
      </p>
      <h1 className="type-display ready-finish-title">Finish your account to keep your {thing}</h1>
      <p className="type-body ready-lead">
        {running
          ? `${link.appName || 'Your app'} is still building. It opens here when it’s done.`
          : `${link.appName || 'Your app'} is ready. One step and it’s yours.`}
      </p>
      <section className="card clay-card ready-card" aria-label="Finish your account">
        <form onSubmit={(e) => void create(e)} noValidate>
          <TextField label="Email" type="email" value={link.email} onChangeText={() => undefined} readOnly />
          <p className="type-caption auth-terms">
            By continuing you agree to the{' '}
            <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
          </p>
          <Button title="Create my account" type="submit" loading={busy} />
        </form>
        <SignUpApple />
        <p className="type-caption ready-note">No password, no code.</p>
      </section>
      <section className="card clay-card ready-app" aria-label={link.appName || 'Your app'}>
        <span className="ready-app-mark" aria-hidden="true">{(link.appName || 'A').trim().charAt(0).toUpperCase()}</span>
        <span className="ready-app-text">
          <span className="type-heading">{link.appName || 'Your app'}</span>
          <span className="type-body ready-app-sub">{running ? 'Still building' : 'Built and waiting for you'}</span>
        </span>
      </section>
    </Screen>
  );
}

/** "or Continue with Apple", when Apple is offered here: back to the build screen, which collects it. */
function SignUpApple() {
  return (
    <div className="ready-apple">
      <p className="social-or type-caption" aria-hidden="true"><span>or</span></p>
      <SignUp card={{ from: '/new/building', only: 'apple' }} />
    </div>
  );
}

/**
 * "This link has expired" — used, or past its three days. With the build on record in this browser,
 * a new one is a field away; otherwise the way back is to sign in, or to start again.
 */
function Expired() {
  const navigate = useNavigate();
  const [intent] = useState(() => readIntent());
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function send(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (sending || !intent?.requestKey) return;
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setProblem('Enter your full email, like you@example.com');
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      const what = useWhat.getState();
      const answer = await requestReadyLink({
        requestKey: intent.requestKey,
        email: address,
        goal: intent.goal,
        appName: intent.appName,
        thing: emailNoun(intent.goal, { demo: what.demo, type: what.type }),
        plan: intent.plan ?? null,
      });
      funnelStep('ready_link_requested', { code: 'sent' in answer ? 'sent' : 'queued' });
      setSentTo(address);
    } catch (err) {
      const refusal = readyLinkRefusal(err);
      funnelStep('error_shown', { where: 'ready_link', code: refusal.code });
      setProblem(refusal.text);
    } finally {
      setSending(false);
    }
  }

  return (
    <Screen title="This link has expired">
      {sentTo ? (
        <p className="type-body ready-lead" role="status">We’ll email {sentTo} a new link{intent?.appName ? ` to ${intent.appName}` : ''}.</p>
      ) : intent?.requestKey ? (
        <>
          <p className="type-body ready-lead">Links work once, for 3 days. Send a new one.</p>
          <form className="ready-card" onSubmit={(e) => void send(e)} noValidate>
            <TextField label="Email" type="email" value={email} onChangeText={setEmail} autoComplete="email" inputMode="email" placeholder="you@example.com" error={problem ?? undefined} />
            <Button title="Send a new one" type="submit" loading={sending} />
          </form>
        </>
      ) : (
        <>
          <p className="type-body ready-lead">Links work once, for 3 days. If you already finished your account, sign in to see your app.</p>
          <Button title="Sign in" onPress={() => navigate('/sign-up', { state: { from: '/my-apps' } })} />
          <p className="ready-wait">
            <button type="button" className="link-button" onClick={() => navigate('/new/what')}>Make a new one</button>
          </p>
        </>
      )}
    </Screen>
  );
}
